import React, { useState } from 'react';
// kojiru-build: yield-simulator-v0.1.0
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  TrendingUp, Calculator, Sparkles, Loader2, Save, RefreshCw,
  DollarSign, BarChart3, AlertTriangle, Activity, Cpu, ArrowRight,
  BookOpen, Sigma, Dice5, Lock, Coins, FileDown
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const COVENANT_OPTIONS = [
  { value: 'LOOSE', label: 'Loose — Wider risk band, higher upside variance' },
  { value: 'STANDARD', label: 'Standard — Platform default' },
  { value: 'STRICT', label: 'Strict — Tighter downside, slight yield trim' },
];

function Stat({ label, value, sub, color = 'text-white', testId }) {
  return (
    <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]" data-testid={testId}>
      <div className="text-[10px] font-mono tracking-[0.2em] text-zinc-500 mb-2">{label}</div>
      <div className={`text-2xl font-mono font-semibold tracking-tight ${color} leading-none`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1.5">{sub}</div>}
    </div>
  );
}

function Chip({ k, v, accent }) {
  return (
    <div className={`font-mono text-[11px] px-3 py-1.5 rounded-full border ${
      accent ? 'bg-purple-500/10 border-purple-500/30 text-purple-300' : 'bg-white/[0.04] border-white/[0.08] text-zinc-400'
    }`}>
      <span className="opacity-60">{k}</span>{' '}
      <span className={`font-semibold ${accent ? 'text-purple-200' : 'text-white'}`}>{v}</span>
    </div>
  );
}

function SensitivityTag({ s }) {
  const delta = s.yield_delta_pct ?? 0;
  const color = delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-zinc-400';
  const symbol = delta > 0 ? '+' : '';
  return (
    <span className="font-mono text-[11px] px-2.5 py-1 rounded-md bg-purple-500/8 border border-purple-500/20 text-purple-300">
      {s.lever} <span className={color}>→ {symbol}{delta}%</span>
      {s.downside_delta_pct !== undefined && (
        <span className="text-emerald-400 ml-1">/ downside {s.downside_delta_pct > 0 ? '+' : ''}{s.downside_delta_pct}%</span>
      )}
    </span>
  );
}

// ─── Math Walkthrough Tab ───
function FormulaBlock({ caption, formula, note, testId }) {
  return (
    <div className="rounded-xl bg-black/40 border border-emerald-500/20 p-4 my-3" data-testid={testId}>
      {caption && (
        <div className="text-[10px] font-mono tracking-[0.22em] text-emerald-400/80 mb-2 uppercase">{caption}</div>
      )}
      <pre className="font-mono text-[13px] text-amber-300 whitespace-pre-wrap leading-relaxed overflow-x-auto">{formula}</pre>
      {note && <div className="text-[11px] text-zinc-500 mt-2 font-mono leading-relaxed">// {note}</div>}
    </div>
  );
}

function MathWalkthrough() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
      data-testid="yield-walkthrough"
    >
      {/* Section: Problem */}
      <section className="mb-12" data-testid="walkthrough-section-question">
        <div className="flex items-center gap-2 mb-3 text-[11px] font-mono tracking-[0.22em] text-cyan-400">
          <BookOpen className="w-4 h-4" /> // THE QUESTION IT ANSWERS
        </div>
        <h2 className="text-2xl font-bold tracking-tight mb-3">
          What&apos;s the realistic dollar yield — and the realistic worst case — of this specific loan?
        </h2>
        <p className="text-zinc-400 leading-relaxed">
          Pro-forma spreadsheets give one deterministic number that hides the entire risk distribution.
          The Kojiru Credit Yield Simulator runs <span className="text-amber-400 font-semibold">10,000 Monte Carlo paths</span> against the target agent&apos;s on-chain ACS profile and returns a committee-ready output card —
          median yield, 95% confidence band, probability of any loss, P99 stress loss, alpha vs.&nbsp;idle USDC, and a fan chart of cumulative P&amp;L over the term.
        </p>
      </section>

      {/* Section: The Math */}
      <section className="mb-12" data-testid="walkthrough-section-math">
        <div className="flex items-center gap-2 mb-3 text-[11px] font-mono tracking-[0.22em] text-cyan-400">
          <Sigma className="w-4 h-4" /> // THE MATH — BOUNDED LOGIC GRAPH
        </div>
        <p className="text-zinc-400 leading-relaxed mb-3">
          Each simulated path is a <span className="text-white font-semibold">discrete-time daily walk</span> over <code className="font-mono text-emerald-400 text-sm px-1.5 py-0.5 bg-emerald-500/8 rounded">term_days</code>. On each day, two stochastic events fire independently.
        </p>

        <h3 className="text-base font-semibold mt-6 mb-1 text-amber-300">A. Daily revenue (the upside)</h3>
        <FormulaBlock
          caption="formula · daily revenue"
          formula="daily_revenue = utilization × line_size × (service_fee_bps / 10000 / 365)"
          note="utilization ~ Normal(0.62, σ) clipped to [0, 1] · σ = agent.behavior_vol × covenant.vol_envelope"
          testId="formula-revenue"
        />

        <h3 className="text-base font-semibold mt-6 mb-1 text-red-300">B. Default events (the downside)</h3>
        <FormulaBlock
          caption="formula · daily default probability"
          formula="p_daily = 1 − (1 − p_30d)^(1 / 30)"
          note="p_30d is read directly from the agent's on-chain ACS profile."
        />
        <FormulaBlock
          caption="formula · loss on default"
          formula="loss = min(escrow_cap, line_size × 0.08) × covenant_multiplier"
          note="Covenant multipliers: 0.62 STRICT · 1.00 STANDARD · 1.35 LOOSE. The escrow cap truncates losses at the smart-contract level — Kojiru's per-task isolated escrow is exhausted before any further exposure."
        />

        <h3 className="text-base font-semibold mt-6 mb-1 text-emerald-300">C. Net P&amp;L and stats</h3>
        <FormulaBlock
          caption="net daily · vectorized across 10,000 paths"
          formula={"daily_net   = daily_revenue − daily_loss\ncumulative  = cumsum(daily_net, axis=1)\nterminal    = cumulative[:, -1]"}
        />

        <div className="rounded-xl border border-white/[0.06] overflow-hidden mt-3" data-testid="walkthrough-metric-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-emerald-500/10 text-left">
                <th className="px-4 py-2.5 text-[11px] font-mono tracking-[0.18em] text-emerald-300 uppercase">Metric</th>
                <th className="px-4 py-2.5 text-[11px] font-mono tracking-[0.18em] text-emerald-300 uppercase">Lender-facing meaning</th>
              </tr>
            </thead>
            <tbody className="font-mono text-[12.5px]">
              {[
                ['P50 / P10 / P90', 'Median + 95% confidence band on dollar outcome'],
                ['P99 loss', '1-in-100 stress-test worst case'],
                ['Prob(any loss)', 'Fraction of paths where cumulative < 0'],
                ['Sharpe-like', '(mean − baseline) / std — risk-adjusted excess'],
                ['Alpha', 'P50 annualized minus USDC staking baseline'],
              ].map(([m, d]) => (
                <tr key={m} className="border-t border-white/[0.05]">
                  <td className="px-4 py-2 text-amber-300 whitespace-nowrap">{m}</td>
                  <td className="px-4 py-2 text-zinc-300">{d}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Section: Monte Carlo */}
      <section className="mb-12" data-testid="walkthrough-section-mc">
        <div className="flex items-center gap-2 mb-3 text-[11px] font-mono tracking-[0.22em] text-cyan-400">
          <Dice5 className="w-4 h-4" /> // WHAT &quot;MONTE CARLO&quot; MEANS
        </div>
        <p className="text-zinc-400 leading-relaxed mb-3">
          When the math is too tangled to solve in closed form, you simulate many random outcomes and count results.
          Named after the Monte Carlo casino — Manhattan Project physicists rolled dice on cards to model neutron diffusion.
          In our engine, one projection = <span className="text-amber-400 font-semibold">900,000 dice rolls</span> (10,000 paths × 90 days), vectorized in NumPy in ~30&nbsp;ms.
        </p>
        <blockquote className="border-l-2 border-amber-500/60 pl-4 py-2 italic text-zinc-300 bg-amber-500/[0.04]">
          &quot;Out of 10,000 alternate universes where I made this exact loan, the median outcome is $3,700; 95% of outcomes fall between $2,800 and $5,400; 1% lose at least $1,200.&quot;
        </blockquote>
      </section>

      {/* Section: LLM Discipline */}
      <section className="mb-12" data-testid="walkthrough-section-discipline">
        <div className="flex items-center gap-2 mb-3 text-[11px] font-mono tracking-[0.22em] text-cyan-400">
          <Lock className="w-4 h-4" /> // ARCHITECTURAL DISCIPLINE — LLM NEVER TOUCHES THE MATH
        </div>
        <p className="text-zinc-400 leading-relaxed mb-4">
          Dollar figures, percentiles, and risk metrics are produced <span className="text-white font-semibold">only</span> by the deterministic NumPy engine.
          The LLM is restricted to composing the natural-language headline at the end. This guarantees:
        </p>
        <ul className="space-y-2.5 text-zinc-300">
          {[
            ['1', 'Same inputs + same seed → bit-identical output, every time.'],
            ['2', 'No model-drift risk on a $10M credit line.'],
            ['3', 'Every projection persists inputs, random_seed, engine_version, and a Checkpoint hash to MongoDB (and V2 on-chain).'],
          ].map(([n, t]) => (
            <li key={n} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-[10px] font-mono text-emerald-400">{n}</span>
              <span className="leading-relaxed">{t}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Section: How lenders get paid */}
      <section className="mb-12" data-testid="walkthrough-section-payment">
        <div className="flex items-center gap-2 mb-3 text-[11px] font-mono tracking-[0.22em] text-cyan-400">
          <Coins className="w-4 h-4" /> // HOW LENDERS ACTUALLY GET PAID
        </div>
        <p className="text-zinc-400 leading-relaxed mb-5">
          The simulator projects the dollar flow of the live on-chain settlement loop in the Agent Credit Protocol.
          Settlement is <span className="text-emerald-400 font-semibold">USDC on Base</span>. No intermediaries, no batch payouts, no 30-day reconciliation.
        </p>
        <ol className="space-y-3">
          {[
            ['Extend', 'Lender extends a credit line to a specific agent — bilateral, isolated, smart-contract-enforced. No pooled capital.'],
            ['Draw', 'Agent draws into per-task escrow when working capital is needed for a deliverable (e.g., paying an API or compute provider).'],
            ['Settle', 'On task completion, escrow auto-settles atomically: principal flows back to the lender, the agreed service fee flows to the lender in the same transaction, Kojiru takes a small protocol cut, and a Checkpoint is written on-chain.'],
            ['Default', 'On default, the smart contract liquidates the escrow — principal first, then service fee — and writes a negative Checkpoint that downgrades the agent\u2019s ACS profile, raising the default probability the next simulator run will read.'],
          ].map(([title, body], i) => (
            <li key={title} className="flex gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid={`walkthrough-step-${i+1}`}>
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 text-black font-mono font-bold flex items-center justify-center">{i+1}</div>
              <div>
                <div className="text-sm font-semibold text-amber-300 tracking-wide mb-0.5">{title}</div>
                <div className="text-sm text-zinc-300 leading-relaxed">{body}</div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* PDF download */}
      <div className="flex flex-wrap items-center gap-3 p-5 rounded-2xl bg-gradient-to-r from-emerald-500/8 via-emerald-500/4 to-transparent border border-emerald-500/20" data-testid="walkthrough-pdf-cta">
        <FileDown className="w-5 h-5 text-emerald-400" />
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-semibold">Download the 2-page Lender Brief</div>
          <div className="text-xs text-zinc-500 font-mono">// kojiru-yield-simulator-explainer.pdf · 39 KB · v1.0.0</div>
        </div>
        <a
          href="/kojiru-yield-simulator-explainer.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-black font-semibold text-sm hover:brightness-110 transition"
          data-testid="walkthrough-pdf-download"
        >
          Download PDF <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </motion.div>
  );
}

export default function YieldSimulator() {
  const [view, setView] = useState('sim'); // 'sim' | 'docs'
  const [form, setForm] = useState({
    target_agent_id: '0xA3F2DEMO123',
    line_size_usdc: 50000,
    service_fee_bps: 1200,
    term_days: 90,
    covenant_tier: 'STANDARD',
    escrow_cap_pct: 100,
    lender_cost_of_capital_bps: 540,
    simulation_paths: 10000,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const runProjection = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('kutro_token');
      if (!token) {
        toast.error('Please sign in as a lender first.');
        setLoading(false);
        return;
      }
      const r = await fetch(`${API}/api/yield-simulator/project`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Projection failed (${r.status})`);
      }
      const data = await r.json();
      setResult(data);
      toast.success(`Projection ${data.projection_id} ready`);
    } catch (e) {
      toast.error(e.message || 'Projection failed');
    } finally {
      setLoading(false);
    }
  };

  const saveProjection = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('kutro_token');
      const r = await fetch(`${API}/api/yield-simulator/projections/${result.projection_id}/save`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Save failed');
      toast.success('Projection saved. Backtest activates when funded.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Build chart data
  const chartData = result?.outputs?.fan_chart?.days?.map((d, i) => ({
    day: d,
    p10: result.outputs.fan_chart.p10[i],
    p50: result.outputs.fan_chart.p50[i],
    p90: result.outputs.fan_chart.p90[i],
  })) || [];

  const out = result?.outputs;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>Credit Yield Simulator™ — Kojiru</title>
        <meta name="description" content="Project net yield, alpha vs USDC, and risk-adjusted return for a single bilateral agent credit line. Deterministic. Reproducible. On-chain." />
      </Helmet>
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 pt-28 pb-20">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-[11px] font-mono tracking-[0.2em] uppercase mb-5" data-testid="yield-eyebrow">
            <Sparkles className="w-3.5 h-3.5" /> Beta — Lender Output Card
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-3" data-testid="yield-title">
            Tell me <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-cyan-400 bg-clip-text text-transparent">how much I&apos;ll earn.</span>
          </h1>
          <p className="text-zinc-400 font-mono text-sm max-w-2xl">
            // Project net yield, alpha vs USDC, and risk-adjusted return for a single bilateral agent credit line. Deterministic. Reproducible. On-chain.
          </p>
        </motion.div>

        {/* ── Tab bar ── */}
        <div className="flex items-center gap-1 mb-8 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06] w-fit" data-testid="yield-tab-bar">
          <button
            onClick={() => setView('sim')}
            className={`px-4 py-2 rounded-lg text-sm font-mono tracking-wider transition ${
              view === 'sim'
                ? 'bg-amber-500 text-black font-semibold'
                : 'text-zinc-400 hover:text-white hover:bg-white/[0.04]'
            }`}
            data-testid="yield-tab-sim"
          >
            <Calculator className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
            Simulator
          </button>
          <button
            onClick={() => setView('docs')}
            className={`px-4 py-2 rounded-lg text-sm font-mono tracking-wider transition ${
              view === 'docs'
                ? 'bg-emerald-500 text-black font-semibold'
                : 'text-zinc-400 hover:text-white hover:bg-white/[0.04]'
            }`}
            data-testid="yield-tab-docs"
          >
            <BookOpen className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
            How It Works
          </button>
        </div>

        {view === 'docs' && <MathWalkthrough />}

        {view === 'sim' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Input panel ── */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-1 space-y-5">
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06]" data-testid="yield-input-panel">
              <div className="flex items-center gap-2 mb-5 text-[11px] font-mono tracking-[0.22em] text-zinc-500">
                <Calculator className="w-4 h-4 text-cyan-400" /> // LENDER INPUTS
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-zinc-400 font-mono tracking-wider mb-1.5 block">target_agent_id</Label>
                  <Input
                    value={form.target_agent_id}
                    onChange={e => setField('target_agent_id', e.target.value)}
                    className="bg-black/30 border-white/10 font-mono text-sm"
                    data-testid="yield-input-agent-id"
                  />
                </div>

                <div>
                  <Label className="text-xs text-zinc-400 font-mono tracking-wider mb-1.5 block">line_size_usdc</Label>
                  <Input
                    type="number" min={100} max={10000000}
                    value={form.line_size_usdc}
                    onChange={e => setField('line_size_usdc', parseInt(e.target.value || 0))}
                    className="bg-black/30 border-white/10 font-mono"
                    data-testid="yield-input-line"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-400 font-mono tracking-wider mb-1.5 block">service_fee_bps</Label>
                    <Input
                      type="number" min={10} max={5000}
                      value={form.service_fee_bps}
                      onChange={e => setField('service_fee_bps', parseInt(e.target.value || 0))}
                      className="bg-black/30 border-white/10 font-mono"
                      data-testid="yield-input-fee"
                    />
                    <div className="text-[10px] text-zinc-500 mt-1 font-mono">= {(form.service_fee_bps/100).toFixed(2)}% APR</div>
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400 font-mono tracking-wider mb-1.5 block">term_days</Label>
                    <Input
                      type="number" min={7} max={730}
                      value={form.term_days}
                      onChange={e => setField('term_days', parseInt(e.target.value || 0))}
                      className="bg-black/30 border-white/10 font-mono"
                      data-testid="yield-input-term"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-zinc-400 font-mono tracking-wider mb-1.5 block">covenant_tier</Label>
                  <Select value={form.covenant_tier} onValueChange={v => setField('covenant_tier', v)}>
                    <SelectTrigger className="bg-black/30 border-white/10 font-mono text-sm" data-testid="yield-input-covenant">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0f] border-white/10">
                      {COVENANT_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value} className="font-mono text-sm">{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-400 font-mono tracking-wider mb-1.5 block">escrow_cap_pct</Label>
                    <Input
                      type="number" min={10} max={100}
                      value={form.escrow_cap_pct}
                      onChange={e => setField('escrow_cap_pct', parseInt(e.target.value || 0))}
                      className="bg-black/30 border-white/10 font-mono"
                      data-testid="yield-input-escrow"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400 font-mono tracking-wider mb-1.5 block">capital_cost_bps</Label>
                    <Input
                      type="number" min={0} max={2000}
                      value={form.lender_cost_of_capital_bps}
                      onChange={e => setField('lender_cost_of_capital_bps', parseInt(e.target.value || 0))}
                      className="bg-black/30 border-white/10 font-mono"
                      data-testid="yield-input-cost"
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={runProjection}
                disabled={loading}
                className="w-full mt-6 h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:brightness-110 text-black font-semibold"
                data-testid="yield-run-button"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running Monte Carlo…</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Project Yield <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>

            {/* Engine signature */}
            <div className="p-4 rounded-xl bg-black/30 border border-white/[0.05] text-[10px] font-mono text-zinc-500 leading-relaxed" data-testid="yield-engine-info">
              <div className="text-zinc-400 mb-1">// ENGINE</div>
              <div>Bounded Logic Graph · numpy Monte Carlo</div>
              <div>Deterministic from (inputs, seed, version)</div>
              <div>Every projection writes an on-chain Checkpoint</div>
            </div>
          </motion.div>

          {/* ── Output card ── */}
          <div className="lg:col-span-2 space-y-5">
            {!out && !loading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-10 rounded-2xl bg-white/[0.02] border border-dashed border-white/[0.08] text-center" data-testid="yield-empty-state">
                <BarChart3 className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400 font-mono text-sm">// Enter lender parameters and run a projection.</p>
                <p className="text-zinc-600 text-xs mt-2">All math is deterministic. All output is reproducible. Every projection is a Checkpoint.</p>
              </motion.div>
            )}

            {loading && (
              <div className="p-10 rounded-2xl bg-white/[0.02] border border-white/[0.08] text-center" data-testid="yield-loading">
                <Loader2 className="w-10 h-10 animate-spin text-amber-500 mx-auto mb-3" />
                <p className="text-zinc-400 font-mono text-sm">Running 10,000 Monte Carlo paths…</p>
              </div>
            )}

            {out && !loading && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="yield-output-card">
                {/* Meta */}
                <div className="flex flex-wrap items-center justify-between gap-3 px-1">
                  <div className="flex flex-wrap gap-2" data-testid="yield-meta-chips">
                    <Chip k="target_agent" v={`${result.target_agent_id.slice(0,8)}… · ACS ${result.acs_profile.acs_score}`} accent />
                    <Chip k="line" v={`$${form.line_size_usdc.toLocaleString()}`} />
                    <Chip k="fee" v={`${form.service_fee_bps} bps`} />
                    <Chip k="term" v={`${form.term_days}d`} />
                    <Chip k="covenant" v={form.covenant_tier} />
                  </div>
                  <div className="font-mono text-[10px] text-zinc-600">
                    <span className="text-emerald-400">● NEW</span> · {result.projection_id} · v{result.engine_version}
                  </div>
                </div>

                {/* Committee headline */}
                <div className="p-6 rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-cyan-500/5" data-testid="yield-headline-card">
                  <div className="text-[10px] font-mono tracking-[0.24em] text-amber-400 mb-3">// COMMITTEE-READY HEADLINE</div>
                  <p className="text-xl leading-relaxed text-white font-medium tracking-tight" data-testid="yield-headline-text">
                    {out.headline}
                  </p>
                </div>

                {/* Stat grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="yield-stat-grid">
                  <Stat label="// NET REVENUE (P50)" value={`$${out.revenue.p50_net_usdc.toLocaleString()}`} sub={`over ${form.term_days} days`} color="text-amber-400" testId="stat-revenue" />
                  <Stat label="// SHARPE vs USDC" value={out.risk.sharpe_vs_usdc.toFixed(2)} sub="risk-adjusted alpha" color={out.risk.sharpe_vs_usdc > 0 ? 'text-emerald-400' : 'text-red-400'} testId="stat-sharpe" />
                  <Stat label="// PROB. ANY LOSS" value={`${out.risk.prob_any_loss_pct.toFixed(1)}%`} sub="of paths net-negative" color={out.risk.prob_any_loss_pct < 10 ? 'text-emerald-400' : 'text-red-400'} testId="stat-loss-prob" />
                  <Stat label="// MAX DRAWDOWN" value={`$${out.risk.expected_max_drawdown_usdc.toLocaleString()}`} sub="worst single-task loss" testId="stat-drawdown" />
                </div>

                {/* Fan chart */}
                <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]" data-testid="yield-chart-card">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-mono tracking-[0.22em] text-zinc-500">
                      // CUMULATIVE NET P&L — {form.term_days}D MONTE CARLO (N={form.simulation_paths.toLocaleString()})
                    </div>
                    <div className="flex gap-3 text-[10px] font-mono">
                      <span className="flex items-center gap-1.5 text-amber-400"><span className="w-2 h-2 rounded-sm bg-amber-400" /> P90</span>
                      <span className="flex items-center gap-1.5 text-cyan-400"><span className="w-2 h-2 rounded-sm bg-cyan-400" /> P50</span>
                      <span className="flex items-center gap-1.5 text-zinc-500"><span className="w-2 h-2 rounded-sm bg-zinc-500" /> P10</span>
                    </div>
                  </div>
                  <div className="w-full h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="day" tick={{ fill: '#52525b', fontSize: 10, fontFamily: 'JetBrains Mono' }} tickFormatter={d => `D${d}`} />
                        <YAxis tick={{ fill: '#52525b', fontSize: 10, fontFamily: 'JetBrains Mono' }} tickFormatter={v => `$${(v/1000).toFixed(1)}k`} />
                        <Tooltip
                          contentStyle={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)', fontFamily: 'JetBrains Mono', fontSize: 12 }}
                          formatter={(v, n) => [`$${Number(v).toLocaleString()}`, n.toUpperCase()]}
                          labelFormatter={l => `Day ${l}`}
                        />
                        <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" strokeDasharray="3 3" />
                        <Area type="monotone" dataKey="p90" stroke="#fbbf24" strokeWidth={2} fillOpacity={0.15} fill="#fbbf24" />
                        <Area type="monotone" dataKey="p50" stroke="#06b6d4" strokeWidth={2.5} fillOpacity={0.12} fill="#06b6d4" />
                        <Area type="monotone" dataKey="p10" stroke="rgba(255,255,255,0.4)" strokeWidth={1.5} strokeDasharray="4 3" fill="none" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Comparison strip */}
                <div className="grid grid-cols-3 gap-3 p-4 rounded-xl border border-emerald-500/20 bg-gradient-to-r from-emerald-500/8 to-transparent" data-testid="yield-comparison-strip">
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.2em] text-zinc-500 mb-1">// THIS DEAL (P50)</div>
                    <div className="text-xl font-mono font-semibold">{out.revenue.p50_annualized_pct.toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.2em] text-zinc-500 mb-1">// USDC STAKING</div>
                    <div className="text-xl font-mono font-semibold text-zinc-400">{out.comparison.usdc_baseline_annualized_pct.toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.2em] text-zinc-500 mb-1">// NET ALPHA</div>
                    <div className={`text-xl font-mono font-semibold ${out.comparison.net_alpha_pct > 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="yield-alpha-value">
                      {out.comparison.net_alpha_pct > 0 ? '+' : ''}{out.comparison.net_alpha_pct.toFixed(1)}% {out.comparison.net_alpha_pct > 0 ? '↑' : '↓'}
                    </div>
                  </div>
                </div>

                {/* Sensitivity */}
                <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]" data-testid="yield-sensitivity">
                  <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mr-2">// SENSITIVITY</span>
                  {out.sensitivity.map((s, i) => <SensitivityTag key={i} s={s} />)}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={saveProjection}
                    disabled={saving}
                    className="flex-1 h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:brightness-110 text-black font-semibold"
                    data-testid="yield-save-button"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save & Track Backtest
                  </Button>
                  <Button
                    onClick={() => { setResult(null); }}
                    variant="outline"
                    className="flex-1 h-12 border-white/10 hover:bg-white/5"
                    data-testid="yield-reset-button"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Adjust Inputs
                  </Button>
                </div>

                {/* Footer signature */}
                <div className="pt-3 border-t border-white/[0.06] flex justify-between text-[10px] font-mono tracking-[0.18em] text-zinc-600" data-testid="yield-footer-sig">
                  <span>KOJIRU.COM · BUILT ON BASE · GENIUS ACT ALIGNED</span>
                  <span className="text-emerald-400">DETERMINISTIC · REPRODUCIBLE · ON-CHAIN</span>
                </div>
              </motion.div>
            )}
          </div>
        </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
